/**
 * „Dokumente & Fotos" auf der Risiko-Detailseite.
 *
 * Zwei Aufgaben: die am Risikoobjekt hängenden Dateien **sichtbar** machen und
 * einen Nachladepfad bieten. Beides ist Pflicht, nicht Kür — der Upload aus der
 * Erfassungsmaske kann teilweise scheitern (Rate-Limit, zu große Datei), und
 * ohne diese Sektion wäre weder das Ergebnis prüfbar noch der Rest nachholbar.
 *
 * Warum nicht der Kontakt-Dokumente-Tab? Uploads **ohne** Kategorie zählen dort
 * nicht zu „Alte Kundenakte", und genau darauf filtert der Explorer-Default —
 * ein frisch hochgeladenes Foto wäre dort per Default unsichtbar. Diese Sektion
 * ist deshalb die primäre Anzeigefläche.
 *
 * Bildvorschau: es gibt **keinen** Thumbnail-Endpunkt, jede Vorschau lädt das
 * Original. Deshalb lazy per `IntersectionObserver` und mit harter
 * Parallelitätsgrenze; Download läuft über `openSignedDownload` (ein nacktes
 * `<a href>` auf `/documents/:id/download` liefe in 401).
 */
import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { documentService, type DocumentDto } from "../../services/documentService";
import { isPreviewImage } from "../documents/documentKind";
import {
  formatBytes,
  pruefeDatei,
  slotTag,
} from "../risikoImmobilieStep/uploadBuffer";
import { BRAND } from "../risikoanalyseStep/tokens";

/** Höchstens zwei Vorschaubilder gleichzeitig laden. */
const VORSCHAU_PARALLEL = 2;

const useStyles = makeStyles({
  wrap: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    fontFamily: BRAND.fontPrimary,
  },
  dropzone: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    width: "100%",
    boxSizing: "border-box",
    textAlign: "left",
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.bg,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "14px",
    paddingRight: "14px",
    ...shorthands.border("1px", "dashed", "#d4cfd9"),
    ...shorthands.borderRadius("6px"),
    ":hover": {
      backgroundColor: "#f5eef6",
      ...shorthands.borderColor(BRAND.purple),
    },
    ":disabled": {
      cursor: "progress",
      opacity: 0.6,
    },
  },
  dropzoneAktiv: {
    backgroundColor: "#f5eef6",
    ...shorthands.borderColor(BRAND.purple),
  },
  versteckt: { display: "none" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
    gap: "10px",
  },
  kachel: {
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "8px",
    paddingRight: "8px",
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", "#e3dee8"),
    ...shorthands.borderRadius("8px"),
  },
  vorschau: {
    height: "92px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.bg,
    ...shorthands.borderRadius("6px"),
    ...shorthands.overflow("hidden"),
  },
  vorschauBild: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  name: {
    fontSize: "0.76rem",
    lineHeight: 1.3,
    color: BRAND.ink,
    wordBreak: "break-word",
  },
  meta: {
    fontSize: "0.7rem",
    color: BRAND.muted,
  },
  aktionen: {
    display: "flex",
    columnGap: "10px",
  },
  linkBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.76rem",
    fontWeight: 600,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    ...shorthands.border("0"),
    ":hover": { textDecorationLine: "underline" },
  },
  loeschBtn: {
    color: "#b91c1c",
  },
  leer: {
    fontSize: "0.86rem",
    fontStyle: "italic",
    color: BRAND.muted,
  },
  fehler: {
    fontSize: "0.8rem",
    color: "#b91c1c",
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
  },
});

const DateiIcon = () => (
  <svg width="24" height="24" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M11.5 2.5H6a1.5 1.5 0 0 0-1.5 1.5v12A1.5 1.5 0 0 0 6 17.5h8a1.5 1.5 0 0 0 1.5-1.5V6.5l-4-4Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
      fill="none"
    />
    <path d="M11.5 2.5v4h4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" fill="none" />
  </svg>
);

/**
 * Der Slot, aus dem eine Datei stammt — aus dem Tag `immo-slot:<key>`
 * zurückgelesen. Rein informativ; ohne Tag bleibt die Zeile leer.
 */
const slotAusTags = (tags: string[] | undefined): string | null => {
  const treffer = (tags ?? []).find((t) => t.startsWith(slotTag("")));
  return treffer ? treffer.slice(slotTag("").length) : null;
};

/**
 * Ein Vorschaubild, das sich erst lädt, wenn es sichtbar ist — und nur, wenn
 * einer der `VORSCHAU_PARALLEL` Plätze frei ist.
 */
const Vorschau = ({
  dokument,
  belegen,
  freigeben,
  styles,
}: {
  dokument: DocumentDto;
  belegen: () => boolean;
  freigeben: () => void;
  styles: ReturnType<typeof useStyles>;
}) => {
  const istBild = isPreviewImage(dokument.fileName, dokument.mimeType);
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [url, setUrl] = React.useState<string | null>(null);
  const [gescheitert, setGescheitert] = React.useState(false);

  React.useEffect(() => {
    if (!istBild || url || gescheitert) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    let abgebrochen = false;
    let wartetAufPlatz: number | null = null;

    const laden = () => {
      if (abgebrochen) return;
      if (!belegen()) {
        // Kein freier Platz — gleich noch einmal versuchen.
        wartetAufPlatz = window.setTimeout(laden, 300);
        return;
      }
      documentService
        .fetchBlob(dokument.id)
        .then((blob) => {
          if (abgebrochen) return;
          setUrl(URL.createObjectURL(blob));
        })
        .catch(() => {
          if (!abgebrochen) setGescheitert(true);
        })
        .finally(freigeben);
    };

    const beobachter = new IntersectionObserver(
      (eintraege) => {
        if (eintraege.some((e) => e.isIntersecting)) {
          beobachter.disconnect();
          laden();
        }
      },
      { rootMargin: "200px" },
    );
    beobachter.observe(el);
    return () => {
      abgebrochen = true;
      beobachter.disconnect();
      if (wartetAufPlatz != null) window.clearTimeout(wartetAufPlatz);
    };
  }, [istBild, url, gescheitert, dokument.id, belegen, freigeben]);

  React.useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );

  return (
    <div className={styles.vorschau} ref={ref}>
      {url ? (
        <img className={styles.vorschauBild} src={url} alt={dokument.fileName} />
      ) : (
        <span style={{ color: BRAND.purple }}>
          <DateiIcon />
        </span>
      )}
    </div>
  );
};

export type RiskDocumentsSectionProps = {
  riskObjectId: number;
  /** Für die Kontaktzuordnung beim Nachladen — sie passiert nur beim Upload. */
  contactId?: number | null;
};

const RiskDocumentsSection = ({ riskObjectId, contactId }: RiskDocumentsSectionProps) => {
  const styles = useStyles();
  const [docs, setDocs] = React.useState<DocumentDto[]>([]);
  const [laedt, setLaedt] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [meldungen, setMeldungen] = React.useState<string[]>([]);
  const [dragAktiv, setDragAktiv] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const belegtRef = React.useRef(0);

  const belegen = React.useCallback(() => {
    if (belegtRef.current >= VORSCHAU_PARALLEL) return false;
    belegtRef.current += 1;
    return true;
  }, []);
  const freigeben = React.useCallback(() => {
    belegtRef.current = Math.max(0, belegtRef.current - 1);
  }, []);

  const laden = React.useCallback(() => {
    setLaedt(true);
    documentService
      .list({ riskObjectId })
      .then(setDocs)
      .catch(() => setDocs([]))
      .finally(() => setLaedt(false));
  }, [riskObjectId]);

  React.useEffect(() => {
    laden();
  }, [laden]);

  const uebernehmen = async (files: FileList | File[] | null) => {
    const liste = files ? Array.from(files) : [];
    if (liste.length === 0) return;
    const abgelehnt: string[] = [];
    const angenommen: File[] = [];
    for (const f of liste) {
      const meldung = pruefeDatei(f, 0);
      if (meldung) abgelehnt.push(meldung);
      else angenommen.push(f);
    }
    setMeldungen(abgelehnt);
    if (angenommen.length === 0) return;
    setBusy(true);
    // Sequenziell — der Upload-Bucket gilt pro IP.
    for (const file of angenommen) {
      try {
        await documentService.upload({
          file,
          riskObjectId,
          contactId: contactId ?? undefined,
        });
      } catch (err) {
        setMeldungen((prev) => [
          ...prev,
          `„${file.name}“: ${err instanceof Error ? err.message : "Upload fehlgeschlagen."}`,
        ]);
      }
    }
    setBusy(false);
    laden();
  };

  const loeschen = async (id: number) => {
    setBusy(true);
    try {
      await documentService.remove(id);
      setDocs((prev) => prev.filter((d) => d.id !== id));
    } catch {
      setMeldungen(["Die Datei konnte nicht gelöscht werden."]);
    } finally {
      setBusy(false);
    }
  };

  const herunterladen = (id: number) => {
    documentService.openSignedDownload(id).catch(() => {
      setMeldungen(["Der Download konnte nicht gestartet werden."]);
    });
  };

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={mergeClasses(styles.dropzone, dragAktiv && styles.dropzoneAktiv)}
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!dragAktiv) setDragAktiv(true);
        }}
        onDragLeave={() => setDragAktiv(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragAktiv(false);
          void uebernehmen(e.dataTransfer?.files ?? null);
        }}
      >
        {busy ? "Wird hochgeladen…" : "Datei auswählen oder hierher ziehen"}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className={styles.versteckt}
        onChange={(e) => {
          void uebernehmen(e.target.files);
          e.target.value = "";
        }}
      />

      {meldungen.length > 0 && (
        <div className={styles.fehler} role="alert">
          {meldungen.map((m, i) => (
            <span key={i}>{m}</span>
          ))}
        </div>
      )}

      {laedt ? (
        <span className={styles.leer}>Dateien werden geladen…</span>
      ) : docs.length === 0 ? (
        <span className={styles.leer}>Noch keine Dokumente oder Fotos an diesem Risiko.</span>
      ) : (
        <div className={styles.grid}>
          {docs.map((d) => {
            const slot = slotAusTags(d.tags);
            return (
              <div key={d.id} className={styles.kachel}>
                <Vorschau
                  dokument={d}
                  belegen={belegen}
                  freigeben={freigeben}
                  styles={styles}
                />
                <span className={styles.name}>{d.fileName}</span>
                <span className={styles.meta}>
                  {[slot, d.fileSize != null ? formatBytes(d.fileSize) : null]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </span>
                <span className={styles.aktionen}>
                  <button
                    type="button"
                    className={mergeClasses(styles.linkBtn)}
                    onClick={() => herunterladen(d.id)}
                  >
                    Herunterladen
                  </button>
                  <button
                    type="button"
                    className={mergeClasses(styles.linkBtn, styles.loeschBtn)}
                    onClick={() => void loeschen(d.id)}
                  >
                    Löschen
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RiskDocumentsSection;
